import { format } from "date-fns";

import { faClock, faTrashCan, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { SortParams } from "@/types/types";
import { classNames } from "@/utils/classNames";

import { BlacklistReasonPopover } from "../components/BlacklistReasonPopover";
import { ContactBlacklistedProspect } from "../../Contacts/schema/contacts";
import { getOrderStatus } from "../../Contacts/utils/column-utils";
import {
	blacklistAttributionLine,
	blacklistDetectedByLabelKey,
	blacklistOriginLabelKey,
	blacklistOriginPillClass,
} from "./blacklistOriginMeta";

export const renderBlacklistSourceColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	return {
		title: ts("blacklist.scopeColumn"),
		width: "fit-content",
		minWidth: "140px",
		uid: "blacklistSource",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.blacklistSource) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			const labels: Record<NonNullable<ContactBlacklistedProspect["blacklistSource"]>, string> = {
				PERSON: ts("blacklist.sourcePerson"),
				ORGANIZATION: ts("blacklist.sourceOrganization"),
				LIST: ts("blacklist.sourceList"),
			};

			// A LIST row comes from a list excluding the lead (competitor scoring, unsubscribe)
			// rather than from a blacklist entry — worth telling apart at a glance.
			const styles: Record<NonNullable<ContactBlacklistedProspect["blacklistSource"]>, string> = {
				PERSON: "bg-red-50 text-red-700 border-red-200",
				ORGANIZATION: "bg-orange-50 text-orange-700 border-orange-200",
				LIST: "bg-gray-50 text-gray-700 border-gray-200",
			};

			return (
				<span
					className={classNames(
						"inline-flex items-center rounded-full px-2 py-0.5 text-xs border",
						styles[row.blacklistSource]
					)}
				>
					{labels[row.blacklistSource]}
				</span>
			);
		},
	};
};

export const renderBlacklistSnoozedColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("blacklist.snoozed"),
		width: "fit-content",
		minWidth: "100px",
		uid: "blacklistSnoozed",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.snoozed) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			return (
				<span className="inline-flex items-center gap-1 text-sm text-text">
					<Icon className="text-text-meta" icon={faClock} />
					{ts("blacklist.snoozedYes")}
				</span>
			);
		},
	};
};

export const renderBlacklistSnoozedUntilColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("blacklist.snoozedUntil"),
		width: "fit-content",
		minWidth: "140px",
		uid: "blacklistSnoozedUntil",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.snoozedUntil) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			const date = new Date(row.snoozedUntil);

			return <span className="text-sm text-text">{date.toLocaleDateString()}</span>;
		},
	};
};

export const renderBlacklistAddedToBlacklistAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	return {
		title: ts("blacklist.addedToBlacklistAt"),
		width: "fit-content",
		minWidth: "160px",
		uid: "blacklistAddedToBlacklistAt",
		orderTitle: "addedToBlacklistAt",
		orderStatus: getOrderStatus(sortingInbox, "addedToBlacklistAt"),
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.addedToBlacklistAt) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			return (
				<span className="text-sm text-text">
					{format(new Date(row.addedToBlacklistAt), "HH:mm dd.MM.yyyy")}
				</span>
			);
		},
	};
};

export const renderBlacklistOriginColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	return {
		title: ts("blacklist.origin"),
		width: "fit-content",
		minWidth: "180px",
		uid: "blacklistOrigin",
		orderTitle: "blacklistOrigin",
		orderStatus: getOrderStatus(sortingInbox, "blacklistOrigin"),
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.blacklistOrigin) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			const label = ts(blacklistOriginLabelKey(row.blacklistOrigin));
			const attribution = blacklistAttributionLine(ts, row);

			// The pill carries the hover the ticket asks for: the reasoning behind the verdict.
			// Only a competitor has one, so everything else keeps a plain pill.
			return (
				<Tooltip
					content={
						<BlacklistReasonPopover
							attribution={attribution}
							reasoning={row.blacklistReason}
							typeLabel={label}
						/>
					}
					delay={300}
					disabled={!row.blacklistReason}
					panel
				>
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2 py-0.5 text-xs border w-fit",
							blacklistOriginPillClass(row.blacklistOrigin)
						)}
					>
						{label}
					</span>
				</Tooltip>
			);
		},
	};
};

/**
 * Who put the row on the blacklist — AI scoring, a person, or the lead themselves (PREC-3594).
 *
 * Distinct from the Scope column beside it, which says *what* is blocked. Both used to be called
 * "Source", which is why the tab could not answer "did we decide this, or did a human?".
 */
export const renderBlacklistDetectedByColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("blacklist.detectedBy"),
		width: "fit-content",
		minWidth: "150px",
		uid: "blacklistDetectedBy",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			const labelKey = blacklistDetectedByLabelKey(row.blacklistDetectedBy);

			if (!labelKey) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			return (
				<span className="inline-flex items-center gap-1.5 text-sm text-text">
					<Icon
						className="text-text-meta"
						icon={row.blacklistDetectedBy === "AI" ? faSparkles : faUser}
					/>
					{ts(labelKey)}
				</span>
			);
		},
	};
};

/** The person behind the row: the entry's creator, or whoever made the competitor call. */
export const renderBlacklistAddedByColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("blacklist.addedBy"),
		width: "fit-content",
		minWidth: "160px",
		uid: "blacklistAddedBy",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactBlacklistedProspect) => {
			if (!row.blacklistAddedBy) {
				return <span className="text-sm text-text-meta">-</span>;
			}

			return <span className="text-sm text-text">{row.blacklistAddedBy}</span>;
		},
	};
};

/**
 * The per-row way off the blacklist (PREC-3594). Removal used to be reachable only by ticking a
 * row and using the footer's bulk action, which is a lot of ceremony for "this one shouldn't be
 * here".
 *
 * A COMPETITOR row deliberately gets no button here: removing its blacklist entry does not stick
 * (the company keeps its Competitor score result and the next scoring run re-adds it), so those
 * rows are served by the "Not a competitor" action instead.
 *
 * An UNSUBSCRIBED_BY_LEAD row gets none either (PREC-2548): an unsubscribe is the lead's own
 * compliance record, the API never removes it, and a button that can only fail is not offered.
 * The cell says why instead.
 */
export const renderBlacklistRemoveColumn = (
	ts: (key: string) => string,
	onRemove: (rowId: number) => void
): TableColumn => ({
	title: ts("blacklist.removeColumn"),
	width: "fit-content",
	minWidth: "180px",
	uid: "blacklistRemove",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: ContactBlacklistedProspect) => {
		if (row.blacklistOrigin === "COMPETITOR") {
			return <span className="text-sm text-text-meta">-</span>;
		}

		if (row.blacklistOrigin === "UNSUBSCRIBED_BY_LEAD") {
			return (
				<Tooltip content={ts("blacklist.removeNotAllowedUnsubscribedHint")} delay={300}>
					<span className="text-sm text-text-meta" data-testid="blacklist-remove-unsubscribed">
						{ts("blacklist.removeNotAllowedUnsubscribed")}
					</span>
				</Tooltip>
			);
		}

		return (
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="w-3 mr-1" icon={faTrashCan} />}
				size={ButtonSize.XS}
				title={ts("blacklist.removeAction")}
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onRemove(row.id);
				}}
			/>
		);
	},
});
